import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Status } from "../../common/constants";
import {
  IAddTemplatePayload,
  ITemplate,
  emptyTemplateForm,
} from "../../common/types/templateTypes";
import useNotification from "../notification/useNotification";
import { useSpinner } from "../../customHooks";
import {
  addTemplate,
  assignOrgTemplates,
  getOrgTemplates,
  getTemplates,
  selector as templatesSelector,
} from "./templatesSlice";

interface UseAssignTemplatesModalProps {
  show: boolean;
  orgId: string | null;
  onClose: () => void;
}

export const useAssignTemplatesModal = ({
  show,
  orgId,
  onClose,
}: UseAssignTemplatesModalProps) => {
  const dispatch = useDispatch<any>();
  const { templates, orgTemplates, status, addStatus, assignStatus, error } =
    useSelector(templatesSelector);
  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const [assignedIds, setAssignedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplate, setNewTemplate] =
    useState<IAddTemplatePayload>(emptyTemplateForm);

  const spinnerStatus =
    status === Status.PENDING ||
    addStatus === Status.PENDING ||
    assignStatus === Status.PENDING
      ? Status.PENDING
      : Status.IDLE;
  useSpinner(spinnerStatus);

  const loadTemplates = useCallback(async () => {
    if (!orgId) return;
    const [orgResult, templatesResult] = await Promise.all([
      dispatch(getOrgTemplates(orgId)),
      dispatch(getTemplates()),
    ]);
    if (getOrgTemplates.rejected.match(orgResult)) {
      showWarningNotification(
        orgResult.payload || "Failed to load organization templates",
      );
    }
    if (getTemplates.rejected.match(templatesResult)) {
      showWarningNotification(
        templatesResult.payload || "Failed to load templates",
      );
    }
  }, [dispatch, orgId]);

  useEffect(() => {
    if (!show || !orgId) return;
    loadTemplates();
  }, [show, orgId, loadTemplates]);

  useEffect(() => {
    setAssignedIds(orgTemplates.map((template) => template.templateId));
  }, [orgTemplates]);

  const toggle = (id: number) => {
    setAssignedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const filteredTemplates = useMemo(
    () =>
      templates.filter(
        (template) =>
          !template.public &&
          template.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [templates, search],
  );

  const resetAddForm = () => {
    setNewTemplate(emptyTemplateForm);
  };

  const handleSave = async () => {
    if (!orgId) return;
    const result = await dispatch(
      assignOrgTemplates({ orgId, templates: assignedIds }),
    );
    if (assignOrgTemplates.fulfilled.match(result)) {
      showSuccessNotification("Templates assigned successfully");
      onClose();
      return;
    }
    showWarningNotification(
      result.payload || error || "Failed to save assignments",
    );
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.templateId) {
      showWarningNotification("Template ID is required");
      return;
    }
    if (!newTemplate.name?.trim() || !newTemplate.file) {
      showWarningNotification("Template name and image (file) are required");
      return;
    }
    const result = await dispatch(addTemplate(newTemplate));
    if (addTemplate.fulfilled.match(result)) {
      showSuccessNotification("Template added");
      setShowAddModal(false);
      resetAddForm();
      await dispatch(getTemplates());
      return;
    }
    showWarningNotification(result.payload || error || "Failed to add template");
  };

  return {
    templates: filteredTemplates as ITemplate[],
    assignedIds,
    search,
    setSearch,
    showAddModal,
    setShowAddModal,
    newTemplate,
    setNewTemplate,
    toggle,
    handleSave,
    handleAddTemplate,
    resetAddForm,
    isBusy:
      status === Status.PENDING ||
      addStatus === Status.PENDING ||
      assignStatus === Status.PENDING,
  };
};
